import * as THREE from 'three';
import { AudioManager } from '../audio/AudioManager';
import { CameraManager } from '../camera/CameraManager';
import { GameLoop } from '../core/GameLoop';
import {
  loadQuality,
  QUALITY_PROFILES,
  saveQuality,
  type QualityLevel,
  type QualityProfile,
} from '../core/quality';
import { NozzleParticles } from '../fx/NozzleParticles';
import { InputManager } from '../input/InputManager';
import { TouchControls } from '../input/TouchControls';
import { PaintSurface } from '../paint/PaintSurface';
import { PaintSystem } from '../paint/PaintSystem';
import { SprayController, type SpraySettings } from '../paint/SprayController';
import { CharacterModel } from '../player/CharacterModel';
import { LocalPlayerController } from '../player/LocalPlayerController';
import { GameState } from '../state/GameState';
import { exportWorld, importWorld } from '../state/serialization';
import type { StrokeEvent } from '../state/strokes';
import { Hud, PALETTE } from '../ui/hud';
import { Overlays } from '../ui/overlays';
import { TouchButtons } from '../ui/touchButtons';
import { Snowfall } from '../snowfall/Snowfall';
import { getLocation } from '../world/LocationManager';
import type { BuiltLocation } from '../world/Location';

interface ActiveWorld {
  built: BuiltLocation;
  paintSystem: PaintSystem;
  snowfall: Snowfall;
  unsubStrokes: () => void;
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

const FADE_INTERVAL = 0.25;
const TOUCH_HINT = 'Drag to look · two fingers to move · pinch to walk · hold ● to spray';

export class Game {
  private renderer: THREE.WebGLRenderer;
  private loop: GameLoop;
  private state = new GameState();
  private input: InputManager;
  private cameras: CameraManager;
  private hud: Hud;
  private overlays: Overlays;
  private audio = new AudioManager();
  private player: LocalPlayerController;
  private character = new CharacterModel();
  private nozzle = new NozzleParticles();
  private spray: SprayController;
  private spraySettings: SpraySettings = { color: PALETTE[0], size: 0.35 };
  private quality: QualityLevel;
  private profile: QualityProfile;
  private world: ActiveWorld | null = null;
  private fadeAccum = 0;
  private takingPhoto = false;
  /** True once touch input is in use — gestures replace pointer lock. */
  private touchMode = false;
  private touchButtons: TouchButtons | null = null;

  constructor(container: HTMLElement) {
    this.quality = loadQuality();
    this.profile = QUALITY_PROFILES[this.quality];

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.profile.maxPixelRatio));
    this.renderer.shadowMap.enabled = this.profile.shadowsEnabled;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    container.appendChild(this.renderer.domElement);
    this.renderer.domElement.addEventListener('contextmenu', (e) => e.preventDefault());

    this.input = new InputManager(this.renderer.domElement);
    this.cameras = new CameraManager(window.innerWidth / window.innerHeight);
    this.state.worldSeed = 1337;

    // Player starts against a placeholder; setLocation() re-targets it.
    this.player = new LocalPlayerController(
      this.input,
      this.state,
      null as unknown as BuiltLocation,
    );
    this.player.sprayColor = this.spraySettings.color;
    this.player.onFootstep = () => this.audio.footstep();
    this.spray = new SprayController(this.state, new PaintSystem(), this.spraySettings);

    this.hud = new Hud(
      container,
      {
        onColorChange: (c) => this.setColor(c),
        onSizeChange: (s) => (this.spraySettings.size = s),
        onSnowfallChange: (v) => this.setSnowfall(v),
        onReset: () => this.requestReset(),
        onCameraToggle: () => this.toggleCamera(),
        onLocations: () => this.showPause(),
        onPhoto: () => this.takePhoto(),
        onHelp: () => this.overlays.showHelp(),
        onMuteToggle: () => this.hud.setMuted(this.audio.toggleMute()),
        onQualityChange: (q) => this.setQuality(q),
      },
      {
        color: this.spraySettings.color,
        size: this.spraySettings.size,
        snowfall: this.state.snowfallIntensity,
        quality: this.quality,
        muted: this.audio.muted,
      },
    );
    this.hud.setVisible(false);
    this.hud.setCameraMode('first');

    this.overlays = new Overlays(
      container,
      {
        onSelectLocation: (id) => this.loadLocation(id),
        onResume: () => this.resume(),
        onExport: () => this.exportWorldFile(),
        onImport: (file) => void this.importWorldFile(file),
      },
      window.matchMedia('(pointer: coarse)').matches,
    );
    this.overlays.showTitle();

    this.wireInput();
    this.wireTouch(container);
    window.addEventListener('resize', () => this.onResize());
    document.addEventListener('pointerlockchange', () => this.onPointerLockChange());

    this.loop = new GameLoop((dt, elapsed) => this.tick(dt, elapsed));
    this.loop.start();
  }

  // ---------------------------------------------------------------- input

  private wireInput(): void {
    const canvas = this.renderer.domElement;
    canvas.addEventListener('mousedown', () => {
      this.audio.ensureStarted();
      if (
        !this.touchMode &&
        this.world &&
        !this.overlays.anyVisible &&
        document.pointerLockElement !== canvas
      ) {
        void canvas.requestPointerLock();
      }
    });

    this.input.events = {
      onLook: (dx, dy) => this.player.applyLook(dx, dy),
      onWheel: (steps) => {
        this.spraySettings.size = THREE.MathUtils.clamp(
          this.spraySettings.size - steps * 0.06,
          0,
          1,
        );
        this.hud.setSize(this.spraySettings.size);
      },
      onPaletteKey: (slot) => {
        const color = PALETTE[slot];
        if (color) {
          this.setColor(color);
          this.hud.setColor(color);
        }
      },
      onHotkey: (key) => this.onHotkey(key),
    };
  }

  private onHotkey(key: 'camera' | 'photo' | 'help' | 'mute' | 'menu' | 'hud'): void {
    switch (key) {
      case 'camera':
        if (this.world) this.toggleCamera();
        break;
      case 'photo':
        if (this.world) this.takePhoto();
        break;
      case 'help':
        if (this.overlays.helpVisible) this.overlays.hideHelp();
        else this.overlays.showHelp();
        break;
      case 'mute':
        this.audio.ensureStarted();
        this.hud.setMuted(this.audio.toggleMute());
        break;
      case 'menu':
        if (this.overlays.helpVisible) this.overlays.hideHelp();
        else if (this.overlays.mainVisible && this.world) this.resume();
        else if (this.world && document.pointerLockElement === null) this.showPause();
        break;
      case 'hud':
        this.hud.root.classList.toggle('collapsed');
        break;
    }
  }

  /** Touch mode: enabled on coarse-pointer devices up front, or on the first
   * real touch (hybrid laptops). Gestures replace pointer-lock mouse-look;
   * on-screen buttons cover spray/jump; gameplay input stays enabled whenever
   * no menu is open (see tick()). */
  private wireTouch(container: HTMLElement): void {
    const enable = () => {
      if (this.touchMode) return;
      this.touchMode = true;
      this.hud.root.classList.add('collapsed');
      this.touchButtons = new TouchButtons(container, {
        onSprayDown: () => {
          this.audio.ensureStarted();
          this.input.setVirtualAction('paint', true);
        },
        onSprayUp: () => this.input.setVirtualAction('paint', false),
        onJump: () => {
          // Press-and-release: consumePressed() picks up the queued jump.
          this.input.setVirtualAction('jump', true);
          this.input.setVirtualAction('jump', false);
        },
      });
      this.touchButtons.setVisible(this.world !== null && !this.takingPhoto);
      new TouchControls(this.renderer.domElement, {
        isActive: () => this.world !== null && !this.overlays.anyVisible && !this.takingPhoto,
        // World-follows-finger, like dragging Google Earth: swipe right looks
        // left, swipe down looks up — consistent with the two-finger pan.
        onLook: (dx, dy) => this.player.applyLook(-dx, -dy),
        onMove: (strafe, forward) => this.player.nudge(strafe, forward),
      });
      this.renderer.domElement.addEventListener('touchstart', () => this.audio.ensureStarted());
      if (this.world) this.hud.showHint(TOUCH_HINT);
    };

    if (window.matchMedia('(pointer: coarse)').matches) enable();
    else window.addEventListener('touchstart', enable, { once: true });
  }

  private onPointerLockChange(): void {
    if (this.touchMode) return;
    const locked = document.pointerLockElement === this.renderer.domElement;
    this.input.enabled = locked;
    if (locked) {
      this.hud.showHint(null);
    } else {
      this.input.clearAll();
      if (this.world && !this.overlays.anyVisible && !this.takingPhoto) {
        this.hud.showHint('Click the snow to keep painting — Esc again for the menu');
      }
    }
  }

  // ------------------------------------------------------------ locations

  loadLocation(id: string, importedStrokes?: StrokeEvent[]): void {
    const def = getLocation(id);
    if (!def) return;
    this.disposeWorld();

    const built = def.build({ seed: (this.state.worldSeed + hashString(id)) >>> 0 });
    const paintSystem = new PaintSystem();
    for (const p of built.paintables) {
      const size = p.large ? this.profile.paintTextureSize : this.profile.paintTextureSizeSmall;
      paintSystem.register(
        new PaintSurface(
          p.id,
          p.mesh,
          size,
          p.mesh.userData.paintUniform as { value: THREE.Texture },
        ),
      );
    }

    const snowfall = new Snowfall({
      seed: (this.state.worldSeed + hashString(id) + 7) >>> 0,
      maxParticles: this.profile.maxSnowParticles,
    });
    built.scene.add(snowfall.points);
    built.scene.add(this.character.group);
    built.scene.add(this.nozzle.points);
    built.scene.add(this.cameras.camera);

    this.state.locationId = id;
    this.state.epoch = performance.now();
    if (importedStrokes) {
      this.state.strokes.replaceAll(importedStrokes.filter((s) => s.locationId === id));
    } else {
      this.state.strokes.clear();
    }

    const unsubStrokes = this.state.strokes.onStroke((s) => paintSystem.applyStroke(s));
    this.world = { built, paintSystem, snowfall, unsubStrokes };

    this.spray = new SprayController(this.state, paintSystem, this.spraySettings);
    this.player.setLocation(built);
    this.cameras.update(this.player, built, 0);

    if (this.state.strokes.count > 0) {
      paintSystem.replay(this.state.strokes.all(), this.renderer);
    }
    this.setSnowfall(this.state.snowfallIntensity);

    this.overlays.hideMain();
    this.hud.setVisible(true);
    this.touchButtons?.setVisible(true);
    if (this.touchMode) {
      this.hud.showHint(TOUCH_HINT);
      window.setTimeout(() => this.hud.showHint(null), 7000);
    } else {
      this.hud.showHint('Click the snow to start painting');
    }
  }

  private disposeWorld(): void {
    if (!this.world) return;
    this.world.unsubStrokes();
    this.world.built.scene.remove(this.character.group, this.nozzle.points, this.cameras.camera);
    this.world.snowfall.dispose();
    this.world.paintSystem.dispose();
    this.world.built.dispose();
    this.world = null;
  }

  private resume(): void {
    this.overlays.hideMain();
    this.hud.setVisible(true);
    if (!this.touchMode) void this.renderer.domElement.requestPointerLock();
  }

  private showPause(): void {
    document.exitPointerLock();
    this.hud.showHint(null);
    this.overlays.showPause();
  }

  // ------------------------------------------------------------- settings

  private setColor(color: string): void {
    this.spraySettings.color = color;
    this.player.sprayColor = color;
    this.cameras.viewCan.setColor(color);
    this.character.setColor(color);
  }

  private setSnowfall(value: number): void {
    this.state.snowfallIntensity = value;
    this.hud.setSnowfall(value);
    if (!this.world) return;
    this.world.snowfall.setIntensity(value);
    const fog = this.world.built.scene.fog as THREE.FogExp2 | null;
    if (fog) fog.density = this.world.built.baseFogDensity * (1 + value * 2.4);
    this.audio.setWindLevel(value);
  }

  private toggleCamera(): void {
    const mode = this.cameras.toggle();
    this.hud.setCameraMode(mode);
  }

  private setQuality(level: QualityLevel): void {
    this.quality = level;
    this.profile = QUALITY_PROFILES[level];
    saveQuality(level);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.profile.maxPixelRatio));
    this.renderer.shadowMap.enabled = this.profile.shadowsEnabled;
    if (this.world) {
      this.world.built.scene.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (mesh.material) {
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          for (const m of mats) m.needsUpdate = true;
        }
        const light = obj as THREE.DirectionalLight;
        if (light.isDirectionalLight && light.shadow) {
          light.shadow.mapSize.setScalar(this.profile.shadowMapSize);
          light.shadow.map?.dispose();
          light.shadow.map = null;
        }
      });
      this.world.snowfall.setMaxParticles(
        this.profile.maxSnowParticles,
        (this.state.worldSeed + hashString(this.state.locationId) + 7) >>> 0,
      );
      // Strokes are the source of truth: rebuild paint textures at the new
      // resolution by replaying the log.
      this.world.paintSystem.resizeAll(
        this.profile.paintTextureSize,
        this.profile.paintTextureSizeSmall,
      );
      this.world.paintSystem.replay(this.state.strokes.all(), this.renderer);
    }
    this.hud.toast(`Quality: ${level}`);
  }

  // -------------------------------------------------------------- actions

  private requestReset(): void {
    this.overlays.confirm('Clear all paint and restore fresh snow?', () => {
      this.state.resetWorld();
      this.world?.paintSystem.clearAll();
      this.hud.toast('Fresh snow — clean canvas.');
    });
  }

  private takePhoto(): void {
    if (!this.world || this.takingPhoto) return;
    this.takingPhoto = true;
    this.hud.setVisible(false);
    this.touchButtons?.setVisible(false);
    // Render one clean frame and capture it in the same task (the WebGL
    // buffer is cleared after compositing otherwise).
    this.world.paintSystem.flush(this.renderer);
    this.renderer.render(this.world.built.scene, this.cameras.camera);
    this.renderer.domElement.toBlob((blob) => {
      if (blob) {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `snow-graffiti-${this.state.locationId}-${Date.now()}.png`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      }
      this.hud.setVisible(true);
      this.touchButtons?.setVisible(true);
      this.hud.toast('Photo saved 📸');
      this.takingPhoto = false;
    });
  }

  private exportWorldFile(): void {
    if (!this.state.locationId) return;
    const json = exportWorld(this.state.locationId, this.state.strokes.all());
    const blob = new Blob([json], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `snow-graffiti-world-${this.state.locationId}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    this.hud.toast('World exported.');
  }

  private async importWorldFile(file: File): Promise<void> {
    try {
      const data = importWorld(await file.text());
      this.loadLocation(data.locationId, data.strokes);
      this.hud.toast(`Imported ${data.strokes.length} strokes.`);
    } catch (err) {
      this.hud.toast(err instanceof Error ? err.message : 'Could not import that file.');
    }
  }

  // ----------------------------------------------------------------- tick

  private tick(dt: number, elapsed: number): void {
    const world = this.world;
    if (!world) return;

    // Touch has no pointer lock; gameplay input is live whenever no menu is open.
    if (this.touchMode) {
      this.input.enabled = !this.overlays.anyVisible && !this.takingPhoto;
    }

    if (this.input.enabled) this.player.update(dt);
    this.player.isSpraying = false;

    this.cameras.update(this.player, world.built, dt);

    // Aim + paint (screen-center raycast, identical in both camera modes).
    this.spray.updateAim(this.cameras.camera);
    const painting = this.input.isDown('paint');
    const nozzlePos = new THREE.Vector3();
    if (this.cameras.mode === 'first') this.cameras.viewCan.nozzleWorldPos(nozzlePos);
    else this.character.nozzleWorldPos(nozzlePos);
    this.spray.update(painting, nozzlePos);
    this.player.isSpraying = this.spray.spraying;
    this.audio.setSpraying(this.spray.spraying);

    if (this.spray.spraying && this.spray.aimHit) {
      this.nozzle.emit(
        nozzlePos,
        this.spray.aimHit.point,
        new THREE.Color(this.spraySettings.color),
        dt,
      );
    }
    this.nozzle.update(dt);

    this.character.group.visible = this.cameras.mode === 'third';
    this.character.update(this.player, dt);

    world.snowfall.update(elapsed, this.cameras.camera, this.renderer.getPixelRatio());

    // Snow accumulation: gradually whiten the paint, proportional to intensity.
    this.fadeAccum += dt;
    if (this.fadeAccum >= FADE_INTERVAL) {
      this.fadeAccum -= FADE_INTERVAL;
      const decay = this.state.snowfallIntensity * 0.012;
      if (decay > 0) world.paintSystem.fadeAll(this.renderer, decay);
    }

    world.paintSystem.flush(this.renderer);
    world.built.update?.(dt, elapsed);

    // Keep the serializable state in sync (what a network layer would send).
    this.state.player = {
      pos: [this.player.position.x, this.player.position.y, this.player.position.z],
      yaw: this.player.yaw,
      pitch: this.player.pitch,
    };

    this.hud.setReticlePaintable(this.spray.aimHit !== null);
    this.renderer.render(world.built.scene, this.cameras.camera);
  }

  private onResize(): void {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.cameras.resize(window.innerWidth / window.innerHeight);
  }
}
